import React from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import WithProcessError from 'hocs/WithProcessError';
import PropTypes from 'prop-types';

import Button from 'components/Button/Button';
import FormGroup from 'components/FormGroup/FormGroup';
import FormItem from 'components/FormItem/FormItem';
import IconButton from 'components/IconButton/IconButton';
import Input from 'components/Input/Input';
import Panel from 'components/Panel/Panel';
import PanelHeader from 'components/PanelHeader/PanelHeader';
import Popup from 'components/Popup/Popup';
import Snackbar from 'components/Snackbar/Snackbar';
import StackedBar from 'components/StackedBar/StackedBar';

import Check24 from 'icons/Check24';
import Close24 from 'icons/Close24';

import useApi from 'utils/api';
import { useAuth } from 'utils/auth';
import { formatDate, getPaymentPeriod } from 'utils/formatAmount';
import { actions } from 'utils/permissions';
import { validateDate, validateNumber } from 'utils/validate';

import styles from './client-page.scss';

function EditSubscription({ onBackClick, panelWidth, data, processError }) {
    const api = useApi();
    const auth = useAuth();
    const { clientId } = useParams();

    const queryClient = useQueryClient();

    const {
        id,
        title,
        client_id,
        created_at,
        customization_id,
        masked_pan,
        regular_amount,
        external_fields,
        paused_at,
        completed_payments_number,
        payments_number,
        is_unlimited,
        once_per_days,
        modified_at,
        status,
    } = data;

    const previousDate = new Date(modified_at);
    const dateThreshold = 1000 * 3600 * 24 * 3; // 3 days

    const [amount, setAmount] = React.useState({ value: regular_amount, error: null });
    const [nextPayment, setNextPayment] = React.useState({ value: dateToString(getNextPaymentDate()), error: null });
    const [loading, setLoading] = React.useState(false);

    const [snack, setSnack] = React.useState(null);
    const [popup, setPopup] = React.useState(null);
    const [closePopup, setClosePopup] = React.useState(false);

    function getNextPaymentDate() {
        const updateDate = new Date(modified_at);
        const currentDate = new Date();

        if (updateDate.getDate() >= currentDate.getDate()) {
            return new Date(currentDate.getFullYear(), currentDate.getMonth(), updateDate.getDate());
        }

        return new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, updateDate.getDate());
    }

    function dateToString(date) {
        const options = {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
        };

        return new Intl.DateTimeFormat('ru-RU', options).format(date);
    }

    function dateFromString(string) {
        const [day, month, year] = string.split('.');

        return new Date(year, month - 1, day);
    }

    function handleDateChange(e) {
        const value = e.target.value.replace(/[^0-9.]/gi, '');
        let error = validateDate(value);

        if (error === null) {
            const date = dateFromString(value);

            if (date.getTime() === previousDate.getTime()) {
                error = null;
            } else if (date.getTime() < Date.now() + dateThreshold) {
                error = new Error('Нельзя приближать дату списания');
            }
        }
        setNextPayment({ value, error });
    }

    function renderExternalFields() {
        if (external_fields) {
            return Object.keys(external_fields).map((key) => {
                const field = external_fields[key];

                return (
                    <FormItem key={`external_field-${key}`} label={field.field_name}>
                        <Input type={'text'} disabled={true} name={`external_field-${key}`} value={field.field_value} />
                    </FormItem>
                );
            });
        }

        return [];
    }

    function handleAmountChange(e) {
        const value = e.target.value;

        updateAmount(value);
    }

    function updateAmount(value) {
        const error = validateNumber(value, 1);

        setAmount({ value, error });
    }

    function validateSettings() {
        return amount.error === null;
    }

    function prepareSettings() {
        return {
            ...data,
            regular_amount: amount.value,
        };
    }

    /**
     * Update subscription settings.
     * For now only payment amount can be edited.
     */
    function update() {
        setLoading(true);

        const companyId = auth?.company.id;

        const settings = prepareSettings();

        if (validateSettings() && companyId) {
            api.updateSubscription(companyId, client_id, id, settings)
                .then((result) => {
                    if (result.updated_subscription) {
                        onSubscriptionUpdated();
                    }
                })
                .catch((error) => processError('Не удалось сохранить изменения', error))
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    function handleCancelClick() {
        setPopup(
            <Popup
                title={'Отменить подписку'}
                close={closePopup}
                maxWidth={360}
                description={'Вы не сможете её восстановить. Клиенту придётся оформить новую.'}
                onClosed={onPopupClose}
                cancelText={'Не сейчас'}
                confirmText={'Да, отменить'}
                confirmDanger={true}
                onConfirm={cancel}
                canCloseOutside={true}
                loading={loading}
            />
        );
    }

    /**
     * Cancel subscription.
     */
    function cancel() {
        setLoading(true);

        const companyId = auth?.company.id;

        api.cancelSubscription(companyId, client_id, id)
            .then((result) => {
                if (result?.cancelled) {
                    onSubscriptionUpdated();
                } else {
                    alert('Не удалось отменить подписку');
                }
            })
            .catch((error) => processError('Не удалось отменить подписку', error))
            .finally(() => {
                setLoading(false);
                setClosePopup(true);
            });
    }

    /**
     * Resume subscription after pause.
     */
    function resume() {
        setLoading(true);

        const companyId = auth?.company.id;

        api.resumeSubscription(companyId, client_id, id)
            .then((result) => {
                if (result?.resumed) {
                    onSubscriptionUpdated();
                }
            })
            .catch((error) => processError('Не удалось возобновить подписку', error))
            .finally(() => {
                handleOnResumedPaused();
                setLoading(false);
            });
    }

    /**
     * Pause subscription.
     */
    function pause() {
        setLoading(true);

        const companyId = auth?.company.id;

        api.pauseSubscription(companyId, client_id, id)
            .then((result) => {
                if (result?.paused) {
                    handleOnResumedPaused();
                    onSubscriptionUpdated();
                }
            })
            .catch((error) => processError('Не удалось приостановить подписку', error))
            .finally(() => {
                setLoading(false);
            });
    }

    /**
     * Shows snackbar after subscription is paused / resumed.
     */
    function handleOnResumedPaused() {
        let text = 'Подписка приостановлена';

        if (isPaused()) {
            text = 'Подписка возобновлена';
        }

        setSnack(<Snackbar text={text} onClosed={onSnackClose} />);
    }

    function onSubscriptionUpdated() {
        queryClient.invalidateQueries({ queryKey: ['subscriptions', 'client-' + clientId] });
    }

    function onSnackClose() {
        setSnack(null);
    }

    function onPopupClose() {
        setPopup(null);
        setClosePopup(false);
    }

    function pauseResumeSubscription() {
        if (isPaused()) {
            resume();
        } else {
            pause();
        }
    }

    function isPaused() {
        return paused_at !== null;
    }

    function isActive() {
        return status === 'ACTIVE';
    }

    function isPausedNow() {
        return status === 'PAUSED';
    }

    const pauseResumeButtonText = isPaused() ? 'Возобновить подписку' : 'Приостановить подписку';
    const paymentsCounterCaption = `Обработано платежей: ${completed_payments_number} из ${payments_number}`;

    React.useEffect(() => {
        updateAmount(data?.regular_amount);
    }, [data]);

    return (
        <Panel
            separated={true}
            noShrink={true}
            contentStyles={{ minWidth: panelWidth, padding: '0 0.5rem' }}
            header={
                <PanelHeader
                    back={true}
                    onBackClick={onBackClick}
                    title={'Подписка'}
                    backIcon={<Close24 />}
                    after={
                        <IconButton
                            icon={<Check24 />}
                            needPermission={actions.EDIT_SUBSCRIPTION}
                            onClick={update}
                            loading={loading}
                            accent={true}
                        />
                    }
                />
            }
        >
            {snack}
            {popup}
            <FormGroup>
                <FormItem
                    label={`Сумма к списанию (руб. / ${getPaymentPeriod(once_per_days)})`}
                    error={amount.error}
                    caption={'Стоимость можно менять за 3 дня до списания.'}
                >
                    <Input
                        type="text"
                        name="amount"
                        onChange={handleAmountChange}
                        disabled={!isActive()}
                        value={amount.value}
                        placeholder={'Например, 149'}
                    />
                </FormItem>
                {renderExternalFields()}
                {auth.hasPermission(actions.CHANGE_PAYMENT_DATE) && (
                    <FormItem label={'Дата следующего списания'} required={true} error={nextPayment.error}>
                        <Input
                            type="text"
                            name="next-payment"
                            placeholder={'дд.мм.гггг'}
                            onChange={handleDateChange}
                            value={nextPayment.value}
                        />
                    </FormItem>
                )}
                {!is_unlimited && (
                    <FormItem label={'Платежи'} caption={paymentsCounterCaption}>
                        <StackedBar filled={completed_payments_number} segments={payments_number} />
                    </FormItem>
                )}
                {(isActive() || isPausedNow()) && (
                    <FormItem>
                        <Button
                            onClick={pauseResumeSubscription}
                            text={pauseResumeButtonText}
                            level={'secondary'}
                            needPermission={actions.PAUSE_RESUME_SUBSCRIPTION}
                            loading={loading}
                        />
                    </FormItem>
                )}
                {isActive() && (
                    <FormItem>
                        <Button
                            onClick={handleCancelClick}
                            danger={true}
                            level={'secondary'}
                            needPermission={actions.CANCEL_SUBSCRIPTION}
                            text={'Отменить подписку'}
                        />
                    </FormItem>
                )}
                <FormItem>
                    <div className={styles.subscriptionTechInfo}>
                        <h3>Сервисная информация</h3>
                        <span>ID подписки: {id}</span>
                        <br />
                        <span>Название: {title}</span>
                        <br />
                        <span>Дата оформления: {formatDate(created_at)}</span>
                        <br />
                        <span>ID формы оплаты: {customization_id}</span>
                        <br />
                        <span>Номер карты: {masked_pan}</span>
                        <br />
                    </div>
                </FormItem>
            </FormGroup>
        </Panel>
    );
}

EditSubscription.propTypes = {
    onBackClick: PropTypes.func,
    panelWidth: PropTypes.number,
    data: PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
        regular_amount: PropTypes.number,
        created_at: PropTypes.string,
        customization_id: PropTypes.number,
        modified_at: PropTypes.string,
        masked_pan: PropTypes.string,
        client_id: PropTypes.number,
        paused_at: PropTypes.string,
        is_unlimited: PropTypes.bool,
        completed_payments_number: PropTypes.number,
        payments_number: PropTypes.number,
        once_per_days: PropTypes.number,
        external_fields: PropTypes.shape({
            [PropTypes.number]: PropTypes.shape({
                field_name: PropTypes.string,
                field_value: PropTypes.string,
            }),
        }),
        status: PropTypes.oneOf(['ACTIVE', 'PAUSED', 'CANCELLED_BY_CLIENT', 'CANCELLED_BY_MANAGER', 'ENDED']),
    }),
    ...WithProcessError.propTypes,
};

export default WithProcessError(EditSubscription);
